import { useState, useEffect, useMemo } from 'react';
import { Filter, Award } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/StructuredData';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Higher grade = higher score. Covers both letter grades and the 9-1 IGCSE scale.
const GRADE_POINTS = {
  'A*': 8, '9': 8,
  'A': 7, '8': 7,
  'B': 6, '7': 6,
  'C': 5, '6': 5,
  'D': 4, '5': 4,
  'E': 3, '4': 3,
  '3': 2, '2': 1, '1': 0, 'U': 0,
};

// Fixed display order for levels within a given year.
const LEVEL_ORDER = ['IGCSE', 'AS', 'A2', 'A Level'];

const getGradeColor = (grade) => {
  if (grade === 'A*' || grade === '9') return 'bg-aspire-red text-white';
  if (grade === 'A' || grade === '8') return 'bg-amber-500 text-white';
  if (grade === 'B' || grade === '7') return 'bg-green-600 text-white';
  return 'bg-gray-500 text-white';
};

// Generic silhouette shown when a student has no photo on file.
const AvatarPlaceholder = () => (
  <svg viewBox="0 0 500 500" className="w-full h-full" aria-hidden="true">
    <rect width="500" height="500" fill="#F0F0F0" />
    <circle cx="250" cy="198" r="110" fill="#404040" />
    <path d="M70 500 C70 328 152 288 250 288 C348 288 430 328 430 500 Z" fill="#404040" />
  </svg>
);

// Group flat result rows into per-student cards, then bucket those cards
// into "IGCSE 2026" / "AS 2026" style sections, each sorted best-results-first.
const buildSections = (results) => {
  const sectionMap = new Map(); // key: `${level}__${year}` -> studentMap
  results.forEach((r) => {
    const sectionKey = `${r.level}__${r.year}`;
    if (!sectionMap.has(sectionKey)) {
      sectionMap.set(sectionKey, { level: r.level, year: r.year, studentMap: new Map() });
    }
    const { studentMap } = sectionMap.get(sectionKey);

    const hasName = r.student_name && r.student_name.trim();
    const studentKey = hasName ? r.student_name.trim().toLowerCase() : `anon-${r.result_id}`;

    if (!studentMap.has(studentKey)) {
      studentMap.set(studentKey, {
        name: hasName ? r.student_name.trim() : 'Anonymous Student',
        photo: null,
        subjects: [],
      });
    }
    const student = studentMap.get(studentKey);
    if (!student.photo && r.image_url) student.photo = r.image_url;
    student.subjects.push({ subject: r.subject, grade: r.grade, id: r.result_id });
  });

  const sections = Array.from(sectionMap.values()).map(({ level, year, studentMap }) => {
    const students = Array.from(studentMap.values()).map((s) => {
      const points = s.subjects.map((sub) => GRADE_POINTS[sub.grade] ?? 0);
      const avgScore = points.reduce((a, b) => a + b, 0) / (points.length || 1);
      const topGradeCount = s.subjects.filter((sub) => sub.grade === 'A*' || sub.grade === '9').length;
      return {
        ...s,
        avgScore,
        topGradeCount,
      };
    });
    students.sort((a, b) =>
      b.topGradeCount - a.topGradeCount ||
      b.avgScore - a.avgScore ||
      b.subjects.length - a.subjects.length ||
      a.name.localeCompare(b.name)
    );
    return { level, year, students };
  });

  sections.sort((a, b) => {
    if (b.year !== a.year) return b.year - a.year;
    return LEVEL_ORDER.indexOf(a.level) - LEVEL_ORDER.indexOf(b.level);
  });

  return sections;
};

const StudentCard = ({ student }) => (
  <div
    className="group bg-white border border-gray-200 hover:shadow-lg transition-all p-5 flex flex-col items-center text-center"
    data-testid={`student-card-${student.name.replace(/\s+/g, '-').toLowerCase()}`}
  >
    <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-100 mb-4 mt-2">
      {student.photo ? (
        <img src={student.photo} alt={student.name} className="w-full h-full object-cover" />
      ) : (
        <AvatarPlaceholder />
      )}
    </div>

    <h3 className="font-heading font-bold text-charcoal text-base mb-4">{student.name}</h3>

    <div className="flex flex-wrap justify-center gap-3">
      {student.subjects
        .slice()
        .sort((a, b) => (GRADE_POINTS[b.grade] ?? 0) - (GRADE_POINTS[a.grade] ?? 0))
        .map((sub) => (
          <div key={sub.id} className="flex flex-col items-center" title={sub.subject}>
            <span className={`w-14 h-14 flex items-center justify-center font-heading font-bold text-xl ${getGradeColor(sub.grade)}`}>
              {sub.grade}
            </span>
            <span className="text-[10px] text-gray-400 mt-1 max-w-[4.5rem] truncate">{sub.subject}</span>
          </div>
        ))}
    </div>
  </div>
);

const ResultsPage = () => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    level: 'all',
    subject: 'all',
    year: 'all',
    examBoard: 'all'
  });

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      try {
        let query = supabase.from('results').select('*');
        if (filters.level !== 'all') query = query.eq('level', filters.level);
        if (filters.subject !== 'all') query = query.eq('subject', filters.subject);
        if (filters.year !== 'all') query = query.eq('year', parseInt(filters.year));
        if (filters.examBoard !== 'all') query = query.eq('exam_board', filters.examBoard);

        const { data, error } = await query;
        if (error) throw error;
        setResults(data);
      } catch (e) {
        console.error('Error fetching results:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [filters]);

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Business', 'Economics', 'Accounting', 'Psychology', 'Law'];
  const years = [2027, 2026, 2025, 2024, 2023, 2022, 2021, 2020];

  // Group results by grade for the top stats bar
  const gradeStats = results.reduce((acc, r) => {
    acc[r.grade] = (acc[r.grade] || 0) + 1;
    return acc;
  }, {});

  const sections = useMemo(() => buildSections(results), [results]);

  return (
    <main data-testid="results-page">
      <SEO
        title="Student Results"
        description="View outstanding IGCSE and A Level examination results from Aspire Academy students. 95% A*/A achievement rate across Cambridge and Edexcel exams."
        path="/results"
      />
      <BreadcrumbSchema items={[{ name: 'Home', href: '/' }, { name: 'Results' }]} />
      {/* Hero */}
      <section className="bg-surface py-16 md:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="label-uppercase text-aspire-red">Student Achievements</span>
            <h1 className="heading-hero mt-2 mb-6">
              Our <span className="text-aspire-red">Results</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Celebrating the outstanding achievements of our students in Cambridge and Edexcel examinations.
              Browse through our results gallery to see the success stories.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-charcoal py-8">
        <div className="container-main">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="font-heading font-bold text-3xl text-white">{results.length}</div>
              <div className="text-gray-400 text-sm">Total Results</div>
            </div>
            <div className="text-center">
              <div className="font-heading font-bold text-3xl text-aspire-red">{gradeStats['A*'] || 0}</div>
              <div className="text-gray-400 text-sm">A* Grades</div>
            </div>
            <div className="text-center">
              <div className="font-heading font-bold text-3xl text-amber-500">{gradeStats['A'] || 0}</div>
              <div className="text-gray-400 text-sm">A Grades</div>
            </div>
            <div className="text-center">
              <div className="font-heading font-bold text-3xl text-green-500">{gradeStats['B'] || 0}</div>
              <div className="text-gray-400 text-sm">B Grades</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & Results */}
      <section className="section-spacing bg-white">
        <div className="container-main">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 mb-12 pb-8 border-b border-gray-200">
            <Filter className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">Filter by:</span>

            <Select value={filters.level} onValueChange={(v) => setFilters({...filters, level: v})}>
              <SelectTrigger className="w-[140px]" data-testid="level-filter">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="IGCSE">IGCSE</SelectItem>
                <SelectItem value="AS">AS Level</SelectItem>
                <SelectItem value="A2">A2 Level</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filters.subject} onValueChange={(v) => setFilters({...filters, subject: v})}>
              <SelectTrigger className="w-[160px]" data-testid="subject-filter">
                <SelectValue placeholder="Subject" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {subjects.map((subj) => (
                  <SelectItem key={subj} value={subj}>{subj}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.year} onValueChange={(v) => setFilters({...filters, year: v})}>
              <SelectTrigger className="w-[120px]" data-testid="year-filter">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filters.examBoard} onValueChange={(v) => setFilters({...filters, examBoard: v})}>
              <SelectTrigger className="w-[140px]" data-testid="exam-board-filter">
                <SelectValue placeholder="Exam Board" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Boards</SelectItem>
                <SelectItem value="Cambridge">Cambridge</SelectItem>
                <SelectItem value="Edexcel">Edexcel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Results by Level & Year */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aspire-red mx-auto"></div>
            </div>
          ) : sections.length > 0 ? (
            <div className="space-y-16">
              {sections.map((section) => (
                <div key={`${section.level}__${section.year}`} data-testid={`results-section-${section.level}-${section.year}`}>
                  <div className="flex items-center gap-4 mb-8">
                    <h2 className="font-heading font-bold text-2xl text-charcoal whitespace-nowrap">
                      {section.level} <span className="text-aspire-red">{section.year}</span>
                    </h2>
                    <div className="h-px bg-gray-200 flex-1" />
                    <span className="text-xs text-gray-400 uppercase tracking-widest">{section.students.length} student{section.students.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pt-3">
                    {section.students.map((student, idx) => (
                      <StudentCard key={`${student.name}-${idx}`} student={student} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No results found matching your filters.</p>
            </div>
          )}
        </div>
      </section>

      {/* Testimonial CTA */}
      <section className="py-16 bg-surface">
        <div className="container-main text-center">
          <h2 className="heading-section mb-4">Join Our Success Stories</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            These results represent the dedication of our students and the expertise of our tutors.
            Start your journey to academic excellence today.
          </p>
          <a
            href="/register"
            className="btn-primary inline-block"
            data-testid="results-register-btn"
          >
            Register Now
          </a>
        </div>
      </section>
    </main>
  );
};

export default ResultsPage;
